import type {
  AIActionKey,
  CustomerActionAllowanceDefinition,
  CustomerActionAllowanceSnapshot,
  CustomerAllowanceCadence,
  PlanTier,
  SubscriptionCatalog,
  SubscriptionSnapshot,
} from '../../../types/src/subscription';
import { subscriptionCatalog } from './catalog';

export interface CustomerAllowanceWindow {
  windowKey: string;
  startsAt: string;
  resetAt: string;
}

function startOfUtcDay(date: Date) {
  return new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()),
  );
}

export function getCustomerAllowanceWindow(
  cadence: CustomerAllowanceCadence,
  now = new Date(),
): CustomerAllowanceWindow {
  const dayStart = startOfUtcDay(now);

  if (cadence === 'day') {
    const reset = new Date(dayStart);
    reset.setUTCDate(reset.getUTCDate() + 1);
    return {
      windowKey: `day:${dayStart.toISOString().slice(0, 10)}`,
      startsAt: dayStart.toISOString(),
      resetAt: reset.toISOString(),
    };
  }

  if (cadence === 'week') {
    const mondayOffset = (dayStart.getUTCDay() + 6) % 7;
    const weekStart = new Date(dayStart);
    weekStart.setUTCDate(weekStart.getUTCDate() - mondayOffset);
    const reset = new Date(weekStart);
    reset.setUTCDate(reset.getUTCDate() + 7);
    return {
      windowKey: `week:${weekStart.toISOString().slice(0, 10)}`,
      startsAt: weekStart.toISOString(),
      resetAt: reset.toISOString(),
    };
  }

  const monthStart = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1),
  );
  const reset = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1),
  );
  return {
    windowKey: `month:${monthStart.toISOString().slice(0, 7)}`,
    startsAt: monthStart.toISOString(),
    resetAt: reset.toISOString(),
  };
}

export function getCustomerAllowanceDefinition(
  actionKey: AIActionKey,
  tier: PlanTier,
  catalog: SubscriptionCatalog = subscriptionCatalog,
): CustomerActionAllowanceDefinition | null {
  return catalog.aiActions[actionKey]?.customerAllowances?.[tier] ?? null;
}

export function buildCustomerAllowanceSnapshot(params: {
  actionKey: AIActionKey;
  tier: PlanTier;
  used?: number;
  reserved?: number;
  now?: Date;
  catalog?: SubscriptionCatalog;
}): CustomerActionAllowanceSnapshot | null {
  const definition = getCustomerAllowanceDefinition(
    params.actionKey,
    params.tier,
    params.catalog,
  );
  if (!definition) {
    return null;
  }

  const used = Math.max(Math.floor(params.used ?? 0), 0);
  const reserved = Math.max(Math.floor(params.reserved ?? 0), 0);
  const window = getCustomerAllowanceWindow(
    definition.cadence,
    params.now,
  );

  return {
    actionKey: params.actionKey,
    ...definition,
    windowKey: window.windowKey,
    used,
    reserved,
    remaining: Math.max(definition.limit - used - reserved, 0),
    resetAt: window.resetAt,
  };
}

export function resolveCustomerActionAllowance(
  snapshot: SubscriptionSnapshot,
  actionKey: AIActionKey,
  catalog: SubscriptionCatalog = subscriptionCatalog,
): CustomerActionAllowanceSnapshot | null {
  const persisted = snapshot.actionAllowances?.[actionKey];
  if (persisted) {
    return persisted;
  }

  return buildCustomerAllowanceSnapshot({
    actionKey,
    tier: snapshot.tier,
    catalog,
  });
}
